import { useEffect, useState } from 'react';
import {
  Sun,
  Coffee,
  Sparkles,
  DollarSign,
  Building2,
  Heart,
  Brain,
  Users,
  Compass,
  Sunrise,
  Flag,
  Clock,
  MapPin,
  ArrowDown,
  Download,
} from 'lucide-react';

const DAYS = [
  {
    id: 'thursday',
    label: 'Thursday',
    date: 'July 23, 2026',
    headline: 'Arrival & Welcome',
    subtitle: 'Setting the sacred space',
    accent: 'from-[#D4AF37] to-[#8C6239]',
    sessions: [
      {
        time: 'Upon arrival',
        title: 'Arrive at The Westin Playa Bonita',
        host: 'Airport shuttle included',
        icon: MapPin,
        bullets: ['Check-in & room assignments'],
      },
      {
        time: 'Lobby check-in',
        title: 'Pick up registration material & The Reset Agenda',
        host: 'Reset Hospitality Desk',
        icon: Flag,
        bullets: [
          'Your printed agenda',
          'Name badge & welcome kit',
          'Schedule cards for your sessions',
        ],
      },
      {
        time: 'Evening',
        title: 'Meet & Greet with the brothers',
        host: 'In person — informal',
        icon: Users,
        bullets: ['Introductions, conversation, and grounding before tomorrow begins'],
      },
    ],
  },
  {
    id: 'friday',
    label: 'Friday',
    date: 'July 24, 2026',
    headline: 'Foundation & Deep Work',
    subtitle: 'Building the brotherhood',
    accent: 'from-[#D4AF37] via-amber-300 to-[#8C6239]',
    sessions: [
      {
        time: '8:15 AM – 9:00 AM',
        title: 'Morning Jump-Start Exercise at the Beach',
        host: 'David G. (LiveNow Fitness)',
        icon: Sunrise,
        bullets: ['Movement, breath, and presence to open the day'],
      },
      {
        time: '9:00 AM – 10:00 AM',
        title: 'Breakfast',
        host: 'Resort dining',
        icon: Coffee,
        bullets: [],
      },
      {
        time: '10:00 AM – 11:00 AM',
        title: 'Kick Off The Reset',
        host: 'Neil Alvanzo',
        icon: Sparkles,
        preWork: true,
        description:
          'When someone asks you what your purpose is, the first response is often uninspired — sometimes upsetting, even frustrating. As a certified Life Purpose Coach, Neil has built a simple, repeatable framework called the Accept U Purpose Finder that connects the scattered dots of your thoughts and behaviors into clarity. Once you can name your purpose, you can align it with your passions and goals — and start living in the most fulfilling, impactful way.',
        bullets: [
          'Why are you here?',
          'The Accept U Purpose-Finder exercise',
          'Why knowing your purpose changes everything',
        ],
      },
      {
        time: '11:00 AM – 12:00 PM',
        title: 'Real Estate: The Proven Tangible Asset',
        host: 'David Harper (Matters of the Mind Vaultage)',
        icon: Building2,
        bullets: [
          'Why real estate sits at the center of generational wealth',
          'Frameworks for evaluating your first (or next) deal',
        ],
      },
      {
        time: '12:30 PM – 2:00 PM',
        title: 'Designing Your Financial Wellness Journey',
        host: 'DeWayne Ellis (The Wealth Syndicate) · Working Lunch',
        icon: DollarSign,
        preWork: true,
        bullets: ['Mapping your financial wellness across the next 12 months'],
      },
      {
        time: '2:15 PM – 3:15 PM',
        title: "The S's of Self Care",
        host: 'David G. (LiveNow Fitness)',
        icon: Heart,
        bullets: [
          'Stillness',
          'Sleep',
          'Support',
          'Strength',
          'Steps',
          'Sitting',
          'Silence',
          'Smiling',
        ],
      },
      {
        time: '4:00 PM – 4:30 PM',
        title: 'Preparation for the Saturday MasterMind Session',
        host: 'Reset Facilitators',
        icon: Brain,
        bullets: [
          'Why a MasterMind?',
          'The format',
          'Goals & planned outcomes',
        ],
      },
    ],
  },
  {
    id: 'saturday',
    label: 'Saturday',
    date: 'July 25, 2026',
    headline: 'Exploration & Integration',
    subtitle: 'Building legacy, taking it home',
    accent: 'from-amber-200 to-[#D4AF37]',
    sessions: [
      {
        time: '8:15 AM – 9:00 AM',
        title: 'Morning Jump-Start Exercise at the Beach',
        host: 'David G. (LiveNow Fitness)',
        icon: Sunrise,
        bullets: [],
      },
      {
        time: '9:30 AM – 10:30 AM',
        title: 'Brotherhood Breakfast',
        host: 'Together at the table',
        icon: Coffee,
        bullets: [],
      },
      {
        time: '10:30 AM – 12:00 PM',
        title: 'MasterMind Session with the Brotherhood',
        host: 'All facilitators · brothers in the room',
        icon: Brain,
        bullets: [
          'Open-table problem solving',
          'Real challenges, real feedback, real strategy',
        ],
      },
      {
        time: '12:00 PM – 1:00 PM',
        title: 'Interested in the Panama Lifestyle?',
        host: 'A conversation with someone living here',
        icon: MapPin,
        bullets: [
          'Real talk: what it actually looks like to live in Panama',
          'Cost of living, residency, community, the day-to-day',
          'Q&A — bring your questions',
        ],
      },
      {
        time: '1:00 PM – 5:00 PM',
        title: "Let's Explore Panama — Half-Day Excursion",
        host: 'Departing after lunch',
        icon: Compass,
        bullets: [
          'Optional · approx. $45–$60 per person',
          'Details will be provided onsite',
        ],
      },
    ],
  },
  {
    id: 'sunday',
    label: 'Sunday',
    date: 'July 26, 2026',
    headline: 'Commitment & Departure',
    subtitle: 'Taking it home',
    accent: 'from-[#D4AF37] to-amber-200',
    sessions: [
      {
        time: '9:00 AM – 10:00 AM',
        title: 'Brotherhood Breakfast',
        host: 'Final shared meal',
        icon: Coffee,
        bullets: [],
      },
      {
        time: '10:00 AM – 10:30 AM',
        title: 'Close-Out Ceremonies at the Beach',
        host: 'All facilitators · all brothers',
        icon: Sun,
        bullets: ['Commitments · pledges · the path forward'],
      },
    ],
  },
];

export default function AgendaPage() {
  const [activeDay, setActiveDay] = useState('thursday');

  useEffect(() => {
    const handleScroll = () => {
      const offsets = DAYS.map((d) => {
        const el = document.getElementById(d.id);
        if (!el) return { id: d.id, top: Number.POSITIVE_INFINITY };
        const rect = el.getBoundingClientRect();
        return { id: d.id, top: Math.abs(rect.top - 120) };
      });
      offsets.sort((a, b) => a.top - b.top);
      if (offsets[0]) setActiveDay(offsets[0].id);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="agenda-page">
      {/* Hero */}
      <section className="relative pt-20 pb-12 px-6 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 10%, #D4AF37 0%, transparent 40%), radial-gradient(circle at 80% 30%, #8C6239 0%, transparent 40%)",
          }}
        />
        <div className="max-w-5xl mx-auto relative z-10">
          <p className="text-[#D4AF37] font-manrope uppercase tracking-[0.3em] text-xs font-bold mb-5">
            The Reset 2026 · Experience Agenda
          </p>
          <h1
            className="font-cormorant text-5xl md:text-7xl font-medium text-white leading-[1.05] mb-6"
            data-testid="agenda-hero-title"
          >
            Four days that will
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D4AF37] to-amber-200 italic">
              re-shape how you show up.
            </span>
          </h1>
          <p className="text-zinc-400 font-manrope text-lg max-w-2xl mb-10">
            July 23–26, 2026 · The Westin Playa Bonita · Panama City, Panama. This is the full
            agenda, top to bottom — every session, every facilitator, every moment.
          </p>

          {/* Day pills */}
          <div className="flex flex-wrap items-center gap-3" data-testid="day-pills">
            {DAYS.map((d) => (
              <button
                key={d.id}
                onClick={() => scrollTo(d.id)}
                className={`px-5 py-2.5 rounded-full font-manrope text-sm font-medium transition-all border ${
                  activeDay === d.id
                    ? 'bg-[#D4AF37] text-[#0A0A0A] border-[#D4AF37]'
                    : 'bg-white/5 text-zinc-300 border-white/10 hover:border-[#D4AF37]/40 hover:text-white'
                }`}
                data-testid={`day-pill-${d.id}`}
              >
                {d.label}
              </button>
            ))}
            <a
              href="https://customer-assets.emergentagent.com/job_analytics-live-1/artifacts/iuaktqnv_TheReset_Panama2026.pdf"
              target="_blank"
              rel="noopener noreferrer"
              download="TheReset_Panama2026_Agenda.pdf"
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A0A0A] px-5 py-2.5 rounded-full font-manrope text-sm font-semibold hover:bg-amber-200 transition-colors"
              data-testid="download-agenda-pdf"
            >
              <Download className="w-4 h-4" />
              Download Agenda PDF
            </a>
            <a
              href="#thursday"
              onClick={(e) => {
                e.preventDefault();
                scrollTo('thursday');
              }}
              className="ml-auto inline-flex items-center gap-2 text-zinc-400 hover:text-[#D4AF37] font-manrope text-sm transition-colors"
            >
              Start the journey <ArrowDown className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* Days */}
      <div className="max-w-5xl mx-auto px-6 pb-24">
        {DAYS.map((day, dayIdx) => (
          <section
            key={day.id}
            id={day.id}
            className="scroll-mt-24 py-16 border-t border-white/5"
            data-testid={`day-${day.id}`}
          >
            {/* Day header */}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
              <div>
                <p className="text-[#D4AF37] font-manrope uppercase tracking-[0.25em] text-xs font-bold mb-3">
                  Day {dayIdx + 1} · {day.date}
                </p>
                <h2 className="font-cormorant text-4xl md:text-5xl font-medium text-white leading-tight">
                  {day.label}: <span className="italic text-zinc-300">{day.headline}</span>
                </h2>
                <p className="text-zinc-500 font-manrope mt-2">{day.subtitle}</p>
              </div>
              <div className={`h-1 w-32 rounded-full bg-gradient-to-r ${day.accent}`} />
            </div>

            {/* Timeline */}
            <div className="relative">
              {/* vertical line */}
              <div className="absolute left-5 top-3 bottom-3 w-px bg-gradient-to-b from-[#D4AF37]/40 via-white/10 to-transparent" />

              <div className="space-y-6">
                {day.sessions.map((s, idx) => {
                  const Icon = s.icon || Clock;
                  return (
                    <div
                      key={idx}
                      className="relative pl-16 group"
                      data-testid={`session-${day.id}-${idx}`}
                    >
                      {/* dot */}
                      <div className="absolute left-0 top-1.5 w-10 h-10 rounded-xl bg-[#111111] border border-[#D4AF37]/30 flex items-center justify-center group-hover:border-[#D4AF37] transition-colors">
                        <Icon className="w-5 h-5 text-[#D4AF37]" />
                      </div>

                      <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 hover:border-[#D4AF37]/30 hover:bg-white/[0.05] transition-all">
                        <div className="flex flex-col md:flex-row md:items-baseline md:justify-between gap-1 mb-2">
                          <h3 className="font-cormorant text-xl md:text-2xl font-medium text-white">
                            {s.title}
                          </h3>
                          <span className="font-manrope text-[#D4AF37] text-sm font-semibold tracking-wide whitespace-nowrap">
                            {s.time}
                          </span>
                        </div>
                        {s.preWork && (
                          <span
                            className="inline-flex items-center gap-1.5 bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/40 rounded-full px-3 py-1 font-manrope text-[11px] uppercase tracking-[0.18em] font-bold mb-3"
                            data-testid="pre-work-badge"
                          >
                            <Flag className="w-3 h-3" />
                            Pre-Work Exercise
                          </span>
                        )}
                        <p className="text-zinc-400 font-manrope text-sm mb-3 italic">
                          {s.host}
                        </p>
                        {s.description && (
                          <p className="text-zinc-300 font-manrope text-sm leading-relaxed mb-3">
                            {s.description}
                          </p>
                        )}
                        {s.bullets.length > 0 && (
                          <ul className="space-y-1.5 mt-3">
                            {s.bullets.map((b, bi) => (
                              <li
                                key={bi}
                                className="flex items-start gap-2 text-zinc-300 font-manrope text-sm leading-relaxed"
                              >
                                <span className="text-[#D4AF37] mt-1">•</span>
                                <span>{b}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        ))}

        {/* Closing */}
        <section className="mt-16 border-t border-white/5 pt-16 text-center">
          <div className="w-16 h-16 rounded-full bg-[#D4AF37]/15 flex items-center justify-center mx-auto mb-6">
            <Heart className="w-8 h-8 text-[#D4AF37]" />
          </div>
          <h2 className="font-cormorant text-3xl md:text-4xl font-medium text-white mb-4">
            Show up open. <span className="italic text-[#D4AF37]">Leave transformed.</span>
          </h2>
          <p className="text-zinc-400 font-manrope max-w-xl mx-auto mb-8">
            This agenda is a guide, not a cage. Real brotherhood lives in the conversations between
            the sessions, the meals, and the late-night talks on the beach.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://customer-assets.emergentagent.com/job_analytics-live-1/artifacts/iuaktqnv_TheReset_Panama2026.pdf"
              target="_blank"
              rel="noopener noreferrer"
              download="TheReset_Panama2026_Agenda.pdf"
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#0A0A0A] px-8 py-3.5 rounded-full font-manrope font-semibold hover:bg-amber-200 transition-colors"
              data-testid="download-agenda-pdf-bottom"
            >
              <Download className="w-5 h-5" />
              Download Agenda PDF
            </a>
            <a
              href="/"
              className="inline-block bg-transparent border border-white/20 text-white px-8 py-3.5 rounded-full font-manrope font-semibold hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
              data-testid="back-home-btn"
            >
              ← Back to The Reset
            </a>
          </div>
        </section>
      </div>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-white/5">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-zinc-600 font-manrope text-sm">
            July 23–26, 2026 · Panama City, Panama · The Westin Playa Bonita
          </p>
        </div>
      </footer>
    </div>
  );
}
